const SUPPORTED_LOCALES = [
	{
		locale: 'en-US',
		language: 'en',
		nativeLabel: 'English (United States)',
		flag: 'us',
		dateFormat: 'MM/dd/yyyy',
		datePickerLocale: 'en',
		aliases: ['en', 'en-us'],
	},
	{
		locale: 'pt-BR',
		language: 'pt',
		nativeLabel: 'Português (Brasil)',
		flag: 'br',
		dateFormat: 'dd/MM/yyyy',
		datePickerLocale: 'pt',
		aliases: ['pt', 'pt-br'],
	},
	{
		locale: 'es-CO',
		language: 'es',
		nativeLabel: 'Español (Colombia)',
		flag: 'co',
		dateFormat: 'dd/MM/yyyy',
		datePickerLocale: 'es',
		aliases: ['es', 'es-co'],
	},
];

const DEFAULT_LOCALE = SUPPORTED_LOCALES[0].locale;
const dateFnsLocaleCache = {};

const normalizeLocaleCase = (value) => {
	if (!value || typeof value !== 'string') {
		return '';
	}

	const normalized = value.trim().replace(/_/g, '-');
	const [language, region, ...rest] = normalized.split('-');

	if (!language) {
		return '';
	}

	const parts = [language.toLowerCase()];

	if (region) {
		parts.push(region.toUpperCase());
	}

	if (rest.length) {
		parts.push(...rest);
	}

	return parts.join('-');
};

const toLookupValue = (value) => normalizeLocaleCase(value).toLowerCase();

const getDefaultLocaleConfig = () => SUPPORTED_LOCALES[0];

const findLocaleConfig = (input) => {
	const lookup = toLookupValue(input);

	if (!lookup) {
		return null;
	}

	return (
		SUPPORTED_LOCALES.find((item) => {
			const localeLookup = toLookupValue(item.locale);
			const languageLookup = toLookupValue(item.language);
			const aliasesLookup = (item.aliases || []).map((alias) =>
				toLookupValue(alias)
			);

			return (
				lookup === localeLookup ||
				lookup === languageLookup ||
				aliasesLookup.includes(lookup)
			);
		}) || null
	);
};

const resolveLocaleConfig = (input) =>
	findLocaleConfig(input) || getDefaultLocaleConfig();

const resolveSupportedLocale = (input) => resolveLocaleConfig(input).locale;

const isSupportedLocale = (input) => {
	const normalizedInput = normalizeLocaleCase(input);

	if (!normalizedInput) {
		return false;
	}

	return SUPPORTED_LOCALES.some(
		(item) => normalizeLocaleCase(item.locale) === normalizedInput
	);
};

const getLanguageFromLocale = (input) => resolveLocaleConfig(input).language;

const getDateFormatForLocale = (input) => resolveLocaleConfig(input).dateFormat;

const getDatePickerLocaleCode = (input) =>
	resolveLocaleConfig(input).datePickerLocale;

const loadDateFnsLocale = (localeCode) => {
	if (!localeCode) {
		return null;
	}

	if (Object.prototype.hasOwnProperty.call(dateFnsLocaleCache, localeCode)) {
		return dateFnsLocaleCache[localeCode];
	}

	try {
		// eslint-disable-next-line global-require
		const localeModule = require(`date-fns/locale/${localeCode}`);
		const locale = localeModule.default || localeModule;

		dateFnsLocaleCache[localeCode] = locale;
		return locale;
	} catch (error) {
		dateFnsLocaleCache[localeCode] = null;
		return null;
	}
};

const getDatePickerLocale = (input) => {
	const localeConfig = resolveLocaleConfig(input);
	const defaultConfig = getDefaultLocaleConfig();

	return (
		loadDateFnsLocale(localeConfig.datePickerLocale) ||
		loadDateFnsLocale(defaultConfig.datePickerLocale)
	);
};

const resolveFallbackChain = (input) => {
	const localeConfig = resolveLocaleConfig(input);
	const defaultConfig = getDefaultLocaleConfig();

	return [
		localeConfig.locale,
		localeConfig.language,
		defaultConfig.locale,
		defaultConfig.language,
	].filter((value, index, arr) => value && arr.indexOf(value) === index);
};

const getSupportedLocaleOptions = () =>
	SUPPORTED_LOCALES.map(
		({
			locale,
			language,
			nativeLabel,
			flag,
			dateFormat,
			datePickerLocale,
		}) => ({
			locale,
			language,
			nativeLabel,
			flag,
			dateFormat,
			datePickerLocale,
		})
	);

const getSupportedLanguageCodes = () =>
	Array.from(new Set(SUPPORTED_LOCALES.map((item) => item.language)));

export {
	DEFAULT_LOCALE,
	SUPPORTED_LOCALES,
	normalizeLocaleCase,
	isSupportedLocale,
	resolveSupportedLocale,
	resolveFallbackChain,
	resolveLocaleConfig,
	getLanguageFromLocale,
	getSupportedLocaleOptions,
	getSupportedLanguageCodes,
	getDateFormatForLocale,
	getDatePickerLocaleCode,
	getDatePickerLocale,
};
